import api from "../../../utils/api";
import type { FirestoreTimestamp } from "./leadsService";

// ============================================================================
// CRM Workflows — frontend service. Talks to the torre-de-control-api
// crmWorkflows module (CRM automations).
//   • list   GET    /crm/workflowsList
//   • single GET    /crm/workflows?id=
//   • create POST   /crm/workflows
//   • update PUT    /crm/workflows?id=
//   • delete DELETE /crm/workflows?id=
// ============================================================================

export type WorkflowTrigger = "lead.created";
export type WorkflowAction = "ai_call";
/** A qué leads aplica: todos los que no tengan un workflow propio, o un formulario. */
export type WorkflowLeadForm = "any" | "demo" | "pilot";

export interface WorkflowConfig {
  crmAgentId: string | null; // which configured CRM agent (crm_agents) runs the call
  agentId: string | null;
  agentPhoneNumberId: string | null;
  fromLabel: string | null;
  onlyWhenPhonePresent: boolean;
  objective: string | null;
  leadForm?: WorkflowLeadForm | null;
}

export interface Workflow {
  id: string;
  name: string;
  description: string | null;
  trigger: WorkflowTrigger;
  action: WorkflowAction;
  enabled: boolean;
  config: WorkflowConfig;
  stats: { triggered: number; completed: number; failed: number };
  createdAt?: FirestoreTimestamp | string | null;
  updatedAt?: FirestoreTimestamp | string | null;
}

export interface WorkflowsPage {
  data: Workflow[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
}

export async function fetchWorkflows(): Promise<WorkflowsPage> {
  const res = await api.get("/crm/workflowsList", { params: { limit: 200 } });
  const body = res.data ?? {};
  return {
    data: Array.isArray(body.data) ? body.data : [],
    totalItems: body.meta?.totalItems ?? 0,
    totalPages: body.meta?.totalPages ?? 1,
    currentPage: body.meta?.currentPage ?? 1,
  };
}

export interface WorkflowInput {
  name: string;
  description?: string | null;
  trigger?: WorkflowTrigger;
  action?: WorkflowAction;
  enabled?: boolean;
  config?: Partial<WorkflowConfig>;
}

export async function createWorkflow(input: WorkflowInput): Promise<Workflow> {
  const res = await api.post("/crm/workflows", input);
  return res.data?.data?.data;
}

export async function updateWorkflow(
  id: string,
  patch: Partial<WorkflowInput>,
): Promise<Workflow> {
  const res = await api.put("/crm/workflows", patch, { params: { id } });
  return res.data?.data?.data;
}

export async function deleteWorkflow(id: string): Promise<void> {
  await api.delete("/crm/workflows", { params: { id } });
}
